import * as React from "react"
import {DrawerSeeker} from "@ui-seeker/core"
import {Grid, TextField, Typography, Alert, ToggleButton, ToggleButtonGroup, Box, FormControlLabel, Checkbox, Accordion, AccordionSummary, AccordionDetails, Tooltip, InputAdornment, Select, FormControl, InputLabel, MenuItem} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"

import {isCreateBatchDrawerOpen, isCreatingBatch, createBatchFormData, activeBatchTab} from "../@signals"
import {SitesAutocomplete, OwnSitesAutocomplete, CustomerAutocomplete, BrandsAutocomplete, SkusAutocomplete, MarketAutocomplete, TagsAutocomplete, CategoriesAutocomplete, TaskEstimate} from "./@components"
import {ALL_BATCH_TYPES, BATCH_TYPE_LABELS, CUSTOMER_REQUIRED_TYPES, PRODUCT_MATCH_TYPES} from "@constants"
import {useCreateBatch} from "../@hooks"


export default function CreateBatchDrawer() {
	const {handleClose, handleCreate, isValid, validationError} = useCreateBatch()
	const isCreating = isCreatingBatch.value
	const batchType = createBatchFormData.value.batchType

	const handleBatchTypeChange = (event, newType) => {
		if (newType !== null) {
			// Calculate default dates for raw2sku
			const today = new Date()
			const sevenDaysAgo = new Date(today)
			sevenDaysAgo.setDate(today.getDate() - 7)
			const formatDate = (date) => date.toISOString().split('T')[0]

			createBatchFormData.value = {
				...createBatchFormData.value,
				batchType: newType,
				// Reset type-specific fields when switching
				markets: newType === 'raw2sku' ? createBatchFormData.value.markets : [],
				skus: newType === 'sku2raw' ? createBatchFormData.value.skus : [],
				dateStart: newType === 'raw2sku' ? formatDate(sevenDaysAgo) : null,
				dateEnd: newType === 'raw2sku' ? formatDate(today) : null
			}
		}
	}

	// Sync batch type with active tab when drawer opens and set default dates
	React.useEffect(() => {
		if (isCreateBatchDrawerOpen.value) {
			const newBatchType = activeBatchTab.value
			const today = new Date()
			const sevenDaysAgo = new Date(today)
			sevenDaysAgo.setDate(today.getDate() - 7)

			// Format dates as YYYY-MM-DD
			const formatDate = (date) => date.toISOString().split('T')[0]

			createBatchFormData.value = {
				...createBatchFormData.value,
				batchType: newBatchType,
				// Set default dates for raw2sku
				dateStart: newBatchType === 'raw2sku' ? formatDate(sevenDaysAgo) : null,
				dateEnd: newBatchType === 'raw2sku' ? formatDate(today) : null
			}
		}
	}, [isCreateBatchDrawerOpen.value])

	return (
		<DrawerSeeker
			title="Create New Batch"
			isOpen={isCreateBatchDrawerOpen.value}
			controller={handleClose}
			successProps={{
				onClick: handleCreate,
				disabled: !isValid || isCreating,
				children: isCreating ? "Creating..." : "Create Batch"
			}}
			cancelProps={{
				disabled: isCreating
			}}
			width="50%"
		>
			<Grid container spacing={3}>
				{/* Batch Type Selector */}
				<Grid item xs={12}>
					<Box sx={{ mb: 1 }}>
						<Typography variant="subtitle2" color="text.secondary" gutterBottom>
							Batch Type
						</Typography>
						<ToggleButtonGroup
							value={batchType}
							exclusive
							onChange={handleBatchTypeChange}
							disabled={isCreating}
							fullWidth
							size="small"
						>
							{/* Driven by ALL_BATCH_TYPES, as batchTypes.js requires: hard-coded,
								the group was missing war2war, so opening the drawer from the
								"Competitor to Own" tab highlighted NO button — and clicking any
								of them to fix the apparently unselected control silently changed
								the direction, with no way back without cancelling. */}
							{ALL_BATCH_TYPES.map(type => (
								<ToggleButton key={type} value={type}>
									{BATCH_TYPE_LABELS[type]}
								</ToggleButton>
							))}
						</ToggleButtonGroup>
					</Box>
				</Grid>

				{/* Batch Name */}
				<Grid item xs={12}>
					<TextField
						fullWidth
						required
						label="Batch Name"
						placeholder="Enter batch name..."
						value={createBatchFormData.value.batchName}
						onChange={e => createBatchFormData.value = {
							...createBatchFormData.value,
							batchName: e.target.value
						}}
						disabled={isCreating}
					/>
				</Grid>

				{/* Exclude products not seen in X days — all batch types.
					    raw2raw/sku2raw: raw candidates; raw2sku: initial raws. */}
				<Grid item xs={12}>
					<FormControl fullWidth size="small">
						<InputLabel id="max-days-since-seen-label">Excluir productos no vistos en</InputLabel>
						<Select
							labelId="max-days-since-seen-label"
							label="Excluir productos no vistos en"
							value={createBatchFormData.value.maxDaysSinceSeen ?? 90}
							disabled={isCreating}
							onChange={e => createBatchFormData.value = {
								...createBatchFormData.value,
								maxDaysSinceSeen: e.target.value
							}}
						>
							<MenuItem value={10}>Últimos 10 días</MenuItem>
							<MenuItem value={30}>Últimos 30 días</MenuItem>
							<MenuItem value={60}>Últimos 60 días</MenuItem>
							<MenuItem value={90}>Últimos 90 días</MenuItem>
						</Select>
					</FormControl>
				</Grid>

				{/* Customer - Required for every customer-scoped direction */}
				{CUSTOMER_REQUIRED_TYPES.includes(batchType) && (
					<Grid item xs={12}>
						<CustomerAutocomplete
							disabled={isCreating}
							clientType={PRODUCT_MATCH_TYPES.includes(batchType) ? 'retailer' : undefined}
						/>
					</Grid>
				)}

				{/* Market - Only for raw2sku (alternative to Sites) */}
				{batchType === 'raw2sku' && (
					<Grid item xs={12}>
						<MarketAutocomplete disabled={isCreating} />
					</Grid>
				)}

				{/* Sites - Multiple selection. For raw2raw these are the competitor TARGET
				    sites; for war2war the competitor sites the ANCHORS come from. Optional in
				    both: empty = all competitor sites. */}
				<Grid item xs={12}>
					<SitesAutocomplete
						disabled={isCreating}
						required={false}
					/>
				</Grid>

				{/* war2war only: which own catalog the new competitor listings are matched
				    against, and how new a listing has to be to count as an anchor. */}
				{batchType === 'war2war' && (
					<>
						<Grid item xs={12}>
							<OwnSitesAutocomplete disabled={isCreating} />
						</Grid>
						<Grid item xs={12}>
							<FormControl fullWidth size="small">
								<InputLabel id="first-date-days-label">Listings first seen in</InputLabel>
								<Select
									labelId="first-date-days-label"
									label="Listings first seen in"
									value={createBatchFormData.value.firstDateDays ?? 30}
									disabled={isCreating}
									onChange={e => createBatchFormData.value = {
										...createBatchFormData.value,
										firstDateDays: e.target.value
									}}
								>
									<MenuItem value={7}>Los últimos 7 días</MenuItem>
									<MenuItem value={15}>Los últimos 15 días</MenuItem>
									<MenuItem value={30}>Los últimos 30 días</MenuItem>
									<MenuItem value={90}>Los últimos 90 días</MenuItem>
								</Select>
							</FormControl>
						</Grid>
					</>
				)}

				{/* Date Range - Only for raw2sku */}
				{batchType === 'raw2sku' && (
					<>
						{/* Date Field Selector */}
						<Grid item xs={12}>
							<Box sx={{ mb: 1 }}>
								<Typography variant="subtitle2" color="text.secondary" gutterBottom>
									Filter by
								</Typography>
								<ToggleButtonGroup
									value={createBatchFormData.value.dateField || 'end_date'}
									exclusive
									onChange={(e, newValue) => {
										if (newValue !== null) {
											createBatchFormData.value = {
												...createBatchFormData.value,
												dateField: newValue
											}
										}
									}}
									disabled={isCreating}
									fullWidth
									size="small"
								>
									<ToggleButton value="end_date">
										Last seen (active products)
									</ToggleButton>
									<ToggleButton value="first_date">
										First seen (new products)
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
						</Grid>
						<Grid item xs={6}>
							<TextField
								fullWidth
								required
								type="date"
								label="Date Start"
								value={createBatchFormData.value.dateStart || ''}
								onChange={(e) => createBatchFormData.value = {
									...createBatchFormData.value,
									dateStart: e.target.value
								}}
								disabled={isCreating}
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								fullWidth
								required
								type="date"
								label="Date End"
								value={createBatchFormData.value.dateEnd || ''}
								onChange={(e) => createBatchFormData.value = {
									...createBatchFormData.value,
									dateEnd: e.target.value
								}}
								disabled={isCreating}
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
						<Grid item xs={12}>
							<FormControlLabel
								control={
									<Checkbox
										checked={createBatchFormData.value.splitByBrands}
										onChange={(e) => createBatchFormData.value = {
											...createBatchFormData.value,
											splitByBrands: e.target.checked,
											splitBySites: e.target.checked ? false : createBatchFormData.value.splitBySites
										}}
										disabled={isCreating}
									/>
								}
								label="Split by brands (create one batch per brand)"
							/>
						</Grid>
						<Grid item xs={12}>
							<FormControlLabel
								control={
									<Checkbox
										checked={createBatchFormData.value.splitBySites}
										onChange={(e) => createBatchFormData.value = {
											...createBatchFormData.value,
											splitBySites: e.target.checked,
											splitByBrands: e.target.checked ? false : createBatchFormData.value.splitByBrands
										}}
										disabled={isCreating}
									/>
								}
								label="Split by sites (create one batch per site)"
							/>
						</Grid>
					</>
				)}

				{/* Brands - Multiple selection */}
				<Grid item xs={12}>
					<BrandsAutocomplete disabled={isCreating} />
				</Grid>

				{/* Categories - Multiple selection (raw2sku / sku2raw only) */}
				{(batchType === 'sku2raw' || batchType === 'raw2sku') && (
					<Grid item xs={12}>
						<CategoriesAutocomplete disabled={isCreating} />
					</Grid>
				)}

				{/* Applies to all directions: the normalizer accepts retry_not_found
				    everywhere, and since the smart retry policy landed this switch is the
				    blunt override on top of it, not the only way to revisit a not_found. */}
				<Grid item xs={12}>
					<FormControlLabel
						control={
							<Checkbox
								checked={!!createBatchFormData.value.retryNotFound}
								onChange={(e) => createBatchFormData.value = {
									...createBatchFormData.value,
									retryNotFound: e.target.checked
								}}
								disabled={isCreating}
							/>
						}
						label="Retry every not-found (ignore the new-candidate check)"
					/>
				</Grid>

				{/* raw2raw-only filters: Top Ventas + Tags */}
				{batchType === 'raw2raw' && (
					<>
						<Grid item xs={12}>
							<TagsAutocomplete disabled={isCreating} />
						</Grid>
						<Grid item xs={12}>
							<FormControlLabel
								control={
									<Checkbox
										checked={!!createBatchFormData.value.topVentas}
										onChange={(e) => createBatchFormData.value = {
											...createBatchFormData.value,
											topVentas: e.target.checked
										}}
										disabled={isCreating}
									/>
								}
								label="Top Ventas only (products flagged as best-sellers)"
							/>
						</Grid>
						<Grid item xs={12}>
							{(() => {
								const fd = createBatchFormData.value
								return (
									<FormControlLabel
										control={
											<Checkbox
												checked={fd.splitByTargetSites !== false}
												onChange={(e) => createBatchFormData.value = {
													...fd,
													splitByTargetSites: e.target.checked
												}}
												disabled={isCreating}
											/>
										}
										label="Split by site (one batch per competitor site)"
									/>
								)
							})()}
						</Grid>
						<Grid item xs={12}>
							<TaskEstimate />
						</Grid>
					</>
				)}

					{/* Auto AI second pass: launches by itself when the batch finishes */}
					<Grid item xs={12}>
						{(() => {
							const asp = createBatchFormData.value.autoSecondPass || {}
						// Calibrated defaults are PER QUEUE TYPE (see second_pass/calibration.py)
						const d = batchType === 'raw2sku' ? { t: 0.9, nf: 0.95 } : { t: 0.8, nf: 0.9 }
							const set = (patch) => createBatchFormData.value = {
								...createBatchFormData.value,
								autoSecondPass: {...asp, ...patch}
							}
							return (
								<>
									<FormControlLabel
										control={
											<Checkbox
												checked={!!asp.enabled}
												onChange={(e) => set({enabled: e.target.checked})}
												disabled={isCreating}
											/>
										}
										label={
											<Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
												AI second pass when the batch finishes (auto-applied)
												<Tooltip title="When the first pass ends, the AI second pass runs by itself with these parameters and applies its decisions directly (validations + not-founds above the thresholds; the rest stays for QA, plus a control sample).">
													<InfoOutlinedIcon sx={{fontSize: 16, color: 'text.secondary'}} />
												</Tooltip>
											</Box>
										}
									/>
									{asp.enabled && (
										<Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1, pl: 4}}>
											<FormControl size="small" sx={{minWidth: 170}}>
												<InputLabel>Model</InputLabel>
												<Select label="Model" value={asp.model || 'gpt-5.6-luna'}
														onChange={(e) => set({model: e.target.value})} disabled={isCreating}>
													<MenuItem value="gpt-5.6-luna">gpt-5.6-luna (recommended)</MenuItem>
													<MenuItem value="gpt-5.1">gpt-5.1</MenuItem>
												</Select>
											</FormControl>
											<TextField size="small" type="number" label="Min confidence" sx={{width: 140}}
												value={asp.threshold ?? d.t} disabled={isCreating}
												inputProps={{step: 0.05, min: 0.5, max: 1}}
												onChange={(e) => set({threshold: e.target.value})} />
											<TextField size="small" type="number" label="Auto not-found ≥" sx={{width: 150}}
												value={asp.nfThreshold ?? d.nf} disabled={isCreating}
												inputProps={{step: 0.05, min: 0.5, max: 1}}
												helperText="Empty = off"
												onChange={(e) => set({nfThreshold: e.target.value})} />
											<TextField size="small" type="number" label="QA sample %" sx={{width: 120}}
												value={asp.samplePct ?? 5} disabled={isCreating}
												inputProps={{step: 1, min: 0, max: 100}}
												onChange={(e) => set({samplePct: e.target.value})} />
											<FormControlLabel
												control={
													<Checkbox size="small" checked={asp.verifyImages !== false}
														onChange={(e) => set({verifyImages: e.target.checked})}
														disabled={isCreating} />
												}
												label={
													<Tooltip title="Turn OFF for competitor sites that reuse one photo for all shades (photo color wrongly vetoes exact text-shade matches).">
														<Box component="span" sx={{fontSize: 14}}>Photos in verify</Box>
													</Tooltip>
												}
											/>
										</Box>
									)}
									{/* Transport for the second pass above: cheap OpenAI-Batch queue by
										default, immediate per-row run when the batch is urgent. */}
									{asp.enabled && (
										<Box sx={{pl: 4}}>
											<FormControlLabel
												control={
													<Checkbox size="small" checked={!!asp.speedMode}
														onChange={(e) => set({speedMode: e.target.checked})}
														disabled={isCreating} />
												}
												label={
													<Box>
														<Typography variant="body2">Speed mode — run the second pass immediately</Typography>
														<Typography variant="caption" color="text.secondary">
															{asp.speedMode
																? 'Results in minutes · full price'
																: 'Off: OpenAI Batch queue · ~50% cheaper · up to 24h until the batch is validated'}
														</Typography>
													</Box>
												}
											/>
										</Box>
									)}
								</>
							)
						})()}
					</Grid>

				{/* SKUs - Multiple selection (only for sku2raw) */}
				{batchType === 'sku2raw' && (
					<Grid item xs={12}>
						<SkusAutocomplete disabled={isCreating} />
					</Grid>
				)}

				{/* Group By */}
				{(batchType === 'sku2raw' || batchType === 'raw2sku') && (
				<Grid item xs={12}>
					<FormControl fullWidth size="small" disabled={isCreating}>
						<InputLabel>Group By</InputLabel>
						<Select
							value={createBatchFormData.value.groupBy || ''}
							label="Group By"
							onChange={(e) => createBatchFormData.value = {
								...createBatchFormData.value,
								groupBy: e.target.value || null
							}}
						>
							<MenuItem value="">No grouping</MenuItem>
							<MenuItem value="reference">Reference</MenuItem>
							<MenuItem value="product_url">Product URL</MenuItem>
						</Select>
					</FormControl>
				</Grid>
				)}

				{/* Advanced: Auto-Validation Thresholds */}
				<Grid item xs={12}>
					<Accordion>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}>
							<Typography variant="subtitle2">
								Advanced: Auto-Validation Thresholds
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<Typography variant="caption" color="text.secondary">
										Leave empty to use system defaults. Controls automatic validation and exclusion during batch processing.
									</Typography>
								</Grid>
								<Grid item xs={12}>
									{(() => {
										const av = createBatchFormData.value.autoValidation || {}
										const asp = createBatchFormData.value.autoSecondPass || {}
										const neverExclude = String(av.maxRateAutoexclude ?? '') === '0'
										// Core brand: nothing may be closed as not_found by the machine. Exclusion
										// threshold 0 (first pass) and second-pass not-found off; low scores go to QA.
										const toggle = (on) => createBatchFormData.value = {
											...createBatchFormData.value,
											autoValidation: {...av, maxRateAutoexclude: on ? '0' : null},
											autoSecondPass: {...asp, nfThreshold: on ? '' : null}
										}
										return (
											<FormControlLabel
												control={<Checkbox checked={neverExclude} onChange={(e) => toggle(e.target.checked)} disabled={isCreating} />}
												label={
													<Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
														Core brand — never exclude
														<Tooltip title="Every item must end up mapped: the first pass never auto-excludes (Max Rate Auto-exclude = 0, items with a low score or no candidate stay in QA) and the AI second pass never closes items as not-found.">
															<InfoOutlinedIcon sx={{fontSize: 16, color: 'text.secondary'}} />
														</Tooltip>
													</Box>
												}
											/>
										)
									})()}
								</Grid>
								<Grid item xs={12} sm={4}>
									<TextField
										fullWidth
										type="number"
										label="Min Rate Auto-validate"
										placeholder="Default: 0.98"
										value={createBatchFormData.value.autoValidation?.minRateAutovalidate ?? ''}
										onChange={(e) => createBatchFormData.value = {
											...createBatchFormData.value,
											autoValidation: {
												...createBatchFormData.value.autoValidation,
												minRateAutovalidate: e.target.value === '' ? null : e.target.value
											}
										}}
										disabled={isCreating}
										inputProps={{ min: 0, max: 1, step: 0.01 }}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<Tooltip title="Minimum AI score to auto-validate (0–1). Default: 0.98">
														<InfoOutlinedIcon fontSize="small" color="action" />
													</Tooltip>
												</InputAdornment>
											)
										}}
									/>
								</Grid>
								<Grid item xs={12} sm={4}>
									<TextField
										fullWidth
										type="number"
										label="Min Gap 2nd Candidate"
										placeholder="Default: 0.05"
										value={createBatchFormData.value.autoValidation?.minGapSecondCandidate ?? ''}
										onChange={(e) => createBatchFormData.value = {
											...createBatchFormData.value,
											autoValidation: {
												...createBatchFormData.value.autoValidation,
												minGapSecondCandidate: e.target.value === '' ? null : e.target.value
											}
										}}
										disabled={isCreating}
										inputProps={{ min: 0, max: 1, step: 0.01 }}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<Tooltip title="Min score gap between top and 2nd candidate. Default: 0.05">
														<InfoOutlinedIcon fontSize="small" color="action" />
													</Tooltip>
												</InputAdornment>
											)
										}}
									/>
								</Grid>
								<Grid item xs={12} sm={4}>
									<TextField
										fullWidth
										type="number"
										label="Max Rate Auto-exclude"
										placeholder="Default: 0.80 (0 = disable)"
										value={createBatchFormData.value.autoValidation?.maxRateAutoexclude ?? ''}
										onChange={(e) => createBatchFormData.value = {
											...createBatchFormData.value,
											autoValidation: {
												...createBatchFormData.value.autoValidation,
												maxRateAutoexclude: e.target.value === '' ? null : e.target.value
											}
										}}
										disabled={isCreating}
										inputProps={{ min: 0, max: 1, step: 0.01 }}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<Tooltip title="Max score to auto-exclude. Set 0 to disable auto-exclusion. Default: 0.80">
														<InfoOutlinedIcon fontSize="small" color="action" />
													</Tooltip>
												</InputAdornment>
											)
										}}
									/>
								</Grid>
							</Grid>
						</AccordionDetails>
					</Accordion>
				</Grid>

				{/* Validation Error */}
				{validationError && !isCreating && (
					<Grid item xs={12}>
						<Alert severity="error">{validationError}</Alert>
					</Grid>
				)}

				{/* Creating Message */}
				{isCreating && (
					<Grid item xs={12}>
						<Alert severity="info">
							<Typography variant="body2">
								<strong>Creating batch...</strong>
							</Typography>
							<Typography variant="body2" sx={{mt: 1}}>
								This process may take a few moments. Please wait while we set up your batch.
							</Typography>
						</Alert>
					</Grid>
				)}

				{/* Info Message */}
				{!isCreating && (
					<Grid item xs={12}>
						<Alert severity="info">
							{batchType === 'sku2raw' ? (
								<>
									<Typography variant="body2">
										<strong>SKU to Product:</strong> Map your SKUs to raw products
									</Typography>
									<Typography variant="body2" sx={{mt: 1}}>
										Required: Batch Name and Customer. Brands and SKUs are optional filters.
									</Typography>
								</>
							) : batchType === 'raw2raw' ? (
								<>
									<Typography variant="body2">
										<strong>Product to Product:</strong> Match your products to competitor products by AI (raw2raw)
									</Typography>
									<Typography variant="body2" sx={{mt: 1}}>
										Required: Batch Name, Customer and at least one Brand. One batch is created per competitor site (all competitor sites by default).
									</Typography>
								</>
							) : batchType === 'war2war' ? (
								<>
									{/* war2war used to fall through this chain to the Product-to-SKU
										text, which asks for a Date Range this direction does not
										have. */}
									<Typography variant="body2">
										<strong>Competitor to Own:</strong> Match new competitor listings against your own catalog by AI (war2war)
									</Typography>
									<Typography variant="body2" sx={{mt: 1}}>
										Required: Batch Name and Customer. Brands, competitor sites and own sites are optional filters; leave Own Sites empty to use every site you have a catalog on.
									</Typography>
								</>
							) : (
								<>
									<Typography variant="body2">
										<strong>Product to SKU:</strong> Map raw products to your SKUs
									</Typography>
									<Typography variant="body2" sx={{mt: 1}}>
										Required: Batch Name and Date Range. Markets/Sites are optional filters.
									</Typography>
								</>
							)}
						</Alert>
					</Grid>
				)}
			</Grid>
		</DrawerSeeker>
	)
}
